import { API_URL } from "../../constants/constants";
import { ResponseDataType, listDataType } from "../../types";
import { apiSlice } from "../api/apiSlice";

const apiWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: [
    "approverList",
    "approverDetails",
    "approverMember",
    "namesBySaasTool",
    "requestList",
    "myRequestList",
    "requestDetails",
    "saasToolByUser",
    "pendingRequestList",
    "myApprovedList",
    "ApprovedRequestList",
    "assigningListOfUser",
    "assignListOfSaaSTool",
    "childRequest",
    "credentialList",
    "myCredentials",
    "myWatchList",
    "requestLicenseInTheQueue",
  ],
});

export const requestSlice = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Creates a new approver.
     * @param formData - The form data containing the approver details.
     * @returns A promise that resolves to the response data.
     */
    createApprover: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.APPROVER,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["approverList", "approverDetails", "approverMember"],
    }),

    /**
     * Fetches a list of approvers from the server.
     *
     * @param search - The search query to filter the approvers.
     * @param limit - The maximum number of approvers to retrieve.
     * @param current_page - The current page of the approver list.
     * @param sort_by - The field to sort the approvers by.
     * @param sort_direction - The direction to sort the approvers in.
     * @param saasTool - The saas tool name to filter the approvers.
     * @param type - The type of approver to filter the approvers.
     * @returns A promise that resolves to the response data containing the list of approvers.
     */

    approverList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        saasTool,
        type,
      }) => ({
        url:
          API_URL.APPROVER +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${saasTool ? `&saas_tool=${saasTool}` : ""
          }${type ? `&type=${type}` : ""}`,
        method: "GET",
      }),
      providesTags: ["approverList"],
    }),

    /**
     * Fetches the details of an approver by its ID.
     * @param id - The ID of the approver to retrieve.
     * @returns A promise that resolves to the response data of the fetched approver.
     */

    approverDetails: builder.query<ResponseDataType, number>({
      query: (id) => ({
        url: API_URL.APPROVER + `/${id}`,
        method: "GET",
      }),
      providesTags: ["approverDetails"],
    }),

    /**
     * Fetches the members of an approver by its ID.
     * @param toolName - The name of the saas tool.
     * @param id - The ID of the approver to retrieve.
     * @returns A promise that resolves to the response data of the fetched approver.
     */

    approverMember: builder.query<ResponseDataType, listDataType>({
      query: ({ toolName, id }) => ({
        url:
          API_URL.APPROVER +
          API_URL.SAAS_TOOL_ITEM_MEMBER +
          `/${toolName}/${id}`,
        method: "GET",
      }),
      providesTags: ["approverMember"],
    }),

    /**
     * Updates an approver.
     * @param id - The ID of the approver to update.
     * @param formData - The form data containing the approver details.
     * @returns A promise that resolves to the response data.
     */

    updateApprover: builder.mutation<
      ResponseDataType,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: API_URL.APPROVER + `/${id}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["approverList", "approverDetails", "approverMember"],
    }),

    /**
     * Deletes an approver.
     * @param id - The ID of the approver to delete.
     * @returns A promise that resolves to the response data.
     */

    deleteApprover: builder.mutation<ResponseDataType, number>({
      query: (id) => ({
        url: API_URL.APPROVER + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["approverList", "approverDetails", "approverMember"],
    }),

    /**
     * Fetches the names of entities by saas tool and type.
     * @param saasTool - The name of the saas tool.
     * @param type - The type of entity.
     * @returns A promise that resolves to the response data containing the list of entity names.
     */

    namesBySaasTool: builder.query<
      ResponseDataType,
      { saasTool: string; type: string }
    >({
      query: ({ saasTool, type }) => ({
        url: API_URL.APPROVER + `/names/${saasTool}/${type}`,
        method: "GET",
      }),
      providesTags: ["namesBySaasTool"],
    }),

    /**
     * Fetches a list of user requests from the server.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @param type - The type of request to filter the requests.
     * @param sub_type - The sub type of request to filter the requests.
     * @param status - The status of the request to filter the requests.
     * @returns A promise that resolves to the response data containing the list of requests.
     */

    requestList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        type,
        sub_type,
        status,
        assignees,
      }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${type ? `&type=${encodeURIComponent(type as string)}` : ""
          }${sub_type
            ? `&sub_type=${encodeURIComponent(sub_type as string)}`
            : ""
          }${status ? `&status=${encodeURIComponent(status as string)}` : ""}${assignees
            ? `&assignees=${encodeURIComponent(assignees as string)}`
            : ""
          }`,
        method: "GET",
      }),
      providesTags: ["requestList"],
    }),

    /**
     * Fetches a list of user requests from the server.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @param type - The type of request to filter the requests.
     * @param sub_type - The sub type of request to filter the requests.
     * @param status - The status of the request to filter the requests.
     * @returns A promise that resolves to the response data containing the list of requests.
     */

    myRequestList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        type,
        sub_type,
        status,
      }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.MY_REQUESTS +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${type ? `&type=${encodeURIComponent(type as string)}` : ""
          }${sub_type
            ? `&sub_type=${encodeURIComponent(sub_type as string)}`
            : ""
          }${status ? `&status=${encodeURIComponent(status as string)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["myRequestList"],
    }),

    /**
     * Creates a new user request.
     * @param formData - The form data containing the request details.
     * @returns A promise that resolves to the response data.
     */

    createUserRequest: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestList",
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Fetches the details of a single request by its ID.
     * @param id - The ID of the request to retrieve.
     * @returns A promise that resolves to the response data of the fetched request
     * @tag requestDetails
     */

    singleRequest: builder.query<ResponseDataType, string>({
      query: (id) => ({
        url: API_URL.REQUEST_MANAGEMENT + `/${id}`,
        method: "GET",
      }),
      providesTags: ["requestDetails"],
    }),

    /**
     * Fetches the SaaS tools assigned to a user.
     * @param email - The email of the user.
     * @returns A promise that resolves to the response data containing the list of SaaS tools.
     */

    getSaaSToolByUser: builder.query<ResponseDataType, string>({
      query: (email) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.USERS +
          "/" +
          email +
          API_URL.SAAS_TOOLS,
        method: "GET",
      }),
      providesTags: ["saasToolByUser"],
    }),

    /**
     * Fetches a list of pending requests from the server.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @returns A promise that resolves to the response data containing the list of pending requests.
     */

    pendingRequestList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.PENDING +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["pendingRequestList"],
    }),

    myApprovedList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.MY_APPROVED_LIST +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["myApprovedList"],
    }),

    /**
     * Fetches a list of approved requests from the server.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @returns A promise that resolves to the response data containing the list of approved requests.
     */

    ApprovedRequestList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction, type, sub_type, assignees }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.APPROVED +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${type ? `&type=${encodeURIComponent(type as string)}` : ""
          }${sub_type ? `&sub_type=${encodeURIComponent(sub_type as string)}` : ""
          }${assignees ? `&assignees=${encodeURIComponent(assignees as string)}` : ""
          }`,
        method: "GET",
      }),
      providesTags: ["ApprovedRequestList"],
    }),

    /**
     * Reviews a request.
     * @param formData - The form data containing the review details.
     * @returns A promise that resolves to the response data.
     */

    reviewRequest: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.REVIEW,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestList",
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "assigningListOfUser",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Changes the status of a request.
     * @param formData - The form data containing the status details.
     * @returns A promise that resolves to the response data.
     */
    changeRequestStatus: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.CHANGE_STATUS,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestList",
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "assigningListOfUser",
        "credentialList",
        "myCredentials",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Fetches the list of entities assigned to a user.
     * @param email - The email of the user.
     * @returns A promise that resolves to the response data containing the list of entities
     */

    assigningListOfUser: builder.query<ResponseDataType, string>({
      query: (email) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.USERS +
          "/" +
          email +
          "/assigning-entities",
        method: "GET",
      }),
      providesTags: ["assigningListOfUser"],
    }),

    /**
     * Fetches the list of entities assigned to a SaaS tool.
     * @param toolName - The name of the SaaS tool.
     * @returns A promise that resolves to the response data containing the list of entities
     */

    assignListOfSaaSTool: builder.query<ResponseDataType, string>({
      query: (toolName) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.SAAS_TOOLS +
          "/" +
          toolName +
          "/assigning-entities",
        method: "GET",
      }),
    }),

    /**
     * Fetches the list of members of an entity.
     * @param toolName - The name of the SaaS tool.
     * @param id - The ID of the entity.
     * @returns A promise that resolves to the response data containing the list of members
     */

    memberOfEntity: builder.query<
      ResponseDataType,
      { toolName: string; id: string }
    >({
      query: ({ toolName, id }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.SAAS_TOOLS +
          "/" +
          toolName +
          "/" +
          id +
          "/members",
        method: "GET",
      }),
    }),

    /**
     * Fetches the sub requests of a request.
     * @param id - The ID of the request.
     * @returns A promise that resolves to the response data containing the list of sub requests
     */

    getChildRequest: builder.query<ResponseDataType, string>({
      query: (id) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.CHILD_REQUEST + `/${id}`,
        method: "GET",
      }),
      providesTags: ["childRequest"],
    }),

    /**
     * Adds a comment to a sub request.
     * @param formData - The form data containing the comment details.
     * @returns A promise that resolves to the response data.
     */

    addComment: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.ADD_COMMENT,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Closes a request.
     * @param formData - The form data containing the request details.
     * @returns A promise that resolves to the response data.
     */

    closeRequest: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.CLOSE_REQUEST,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "childRequest",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Cancels a request.
     * @param formData - The form data containing the request details.
     * @returns A promise that resolves to the response data.
     */
    cancelRequest: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.CANCEL_REQUEST,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestList",
        "myRequestList",
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "childRequest",
        "myWatchList",
        "requestLicenseInTheQueue",
      ],
    }),

    /**
     * Syncs the watchers of a request.
     * @param formData - The form data containing the request details.
     * @returns A promise that resolves to the response data.
     */
    syncWatchers: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.WATCHERS,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "childRequest",
        "myWatchList",
      ],
    }),

    /**
     * Fetches the list of requests in the watch list.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @param type - The type of request to filter the requests.
     * @param sub_type - The sub type of request to filter the requests.
     * @param status - The status of the request to filter the requests.
     * @returns A promise that resolves to the response data containing the list of requests.
     */

    myWatchList: builder.query<ResponseDataType, listDataType>({
      query: ({
        search,
        limit,
        current_page,
        sort_by,
        sort_direction,
        type,
        sub_type,
        status,
      }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          API_URL.WATCH_LIST +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${type ? `&type=${encodeURIComponent(type as string)}` : ""
          }${sub_type
            ? `&sub_type=${encodeURIComponent(sub_type as string)}`
            : ""
          }${status ? `&status=${encodeURIComponent(status as string)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["myWatchList"],
    }),

    /**
     * Fetches the list of requests in the queue.
     * @param search - The search query to filter the requests.
     * @param limit - The maximum number of requests to retrieve.
     * @param current_page - The current page of the request list.
     * @param sort_by - The field to sort the requests by.
     * @param sort_direction - The direction to sort the requests in.
     * @returns A promise that resolves to the response data containing the list of requests.
     */

    getRequestLicenseInTheQueue: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
        url:
          API_URL.REQUEST_MANAGEMENT +
          `/requested-license?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["requestLicenseInTheQueue"],
    }),

    assignAssignee: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.REQUEST_MANAGEMENT + API_URL.ASSIGN_ASSIGNEE,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "requestList",
        "requestDetails",
        "pendingRequestList",
        "myApprovedList",
        "ApprovedRequestList",
        "myRequestList",
        "assigningListOfUser",
        "childRequest",
        "myWatchList",
      ],
    }),
  }),
});

export const {
  useCreateApproverMutation,
  useApproverListQuery,
  useApproverDetailsQuery,
  useApproverMemberQuery,
  useUpdateApproverMutation,
  useDeleteApproverMutation,
  useNamesBySaasToolQuery,
  useCreateUserRequestMutation,
  useRequestListQuery,
  useMyRequestListQuery,
  useSingleRequestQuery,
  useGetSaaSToolByUserQuery,
  usePendingRequestListQuery,
  useMyApprovedListQuery,
  useReviewRequestMutation,
  useApprovedRequestListQuery,
  useChangeRequestStatusMutation,
  useAssigningListOfUserQuery,
  useAssignListOfSaaSToolQuery,
  useMemberOfEntityQuery,
  useAddCommentMutation,
  useGetChildRequestQuery,
  useCloseRequestMutation,
  useCancelRequestMutation,
  useSyncWatchersMutation,
  useMyWatchListQuery,
  useGetRequestLicenseInTheQueueQuery,
  useAssignAssigneeMutation,
} = requestSlice;
